import { ReactElement, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, size, useFloating } from "@floating-ui/react";
import { GroupBase, MenuPortalProps } from "react-select";
import {
  MENU_AVAILABLE_HEIGHT_VAR,
  MENU_AVAILABLE_WIDTH_VAR,
  MENU_VIEWPORT_GUTTER_PX,
} from "common/components/Dropdown/DropdownStyles";

/** Gap between the control and the open menu, matching react-select's default `menuGutter`. */
const MENU_GAP_PX = 8;

/**
 * `MenuPortal` for react-select that positions the menu with Floating UI's visualViewport-aware
 * `fixed` strategy. react-select's own portal positions against the layout viewport only, so its
 * menu detaches from the control when a mobile keyboard resizes the visual viewport.
 *
 * @param props
 * @returns
 */
export const DropdownMenuPortal = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MenuPortalProps<Option, IsMulti, Group>,
): ReactElement | null => {
  const { appendTo, children, controlElement, innerProps, menuPlacement, cx, getClassNames } = props;

  const { refs, floatingStyles, isPositioned } = useFloating<HTMLDivElement>({
    strategy: "fixed",
    placement: menuPlacement === "top" ? "top-start" : "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(MENU_GAP_PX),
      flip({ padding: MENU_VIEWPORT_GUTTER_PX }),
      size({
        padding: MENU_VIEWPORT_GUTTER_PX,
        apply({ availableWidth, availableHeight, rects, elements }) {
          // Floating UI's visualViewport-aware room to the viewport edges, in the current placement.
          elements.floating.style.width = `${rects.reference.width}px`;
          elements.floating.style.setProperty(MENU_AVAILABLE_WIDTH_VAR, `${Math.max(0, availableWidth)}px`);
          elements.floating.style.setProperty(MENU_AVAILABLE_HEIGHT_VAR, `${Math.max(0, availableHeight)}px`);
        },
      }),
    ],
  });

  useLayoutEffect(() => {
    refs.setReference(controlElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `refs.setReference` is stable; `refs` identity is not.
  }, [controlElement]);

  const className = cx({ "menu-portal": true }, getClassNames("menuPortal", props));

  const menuWrapper = (
    <div
      ref={refs.setFloating}
      className={className}
      style={{ ...floatingStyles, visibility: isPositioned ? undefined : "hidden" }}
      {...innerProps}>
      {children}
    </div>
  );

  // Match react-select: under `menuPosition="fixed"` it renders inline when there's no portal target.
  return appendTo ? createPortal(menuWrapper, appendTo) : menuWrapper;
};
